import "react";
import PageHeader from "../../componentes/PageHeader";
import MetaTags from "../../componentes/MetaTags";
import GradientBanner from "../../componentes/Banner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faBug,
  faExclamationTriangle,
  faChartBar,
  faBell,
  faTools,
  faCogs,
  faDatabase,
  faFileAlt,
  faShieldAlt,
  faUsersCog,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import "../Home/services.css";
import "../../styles/circuit-background.css";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const OurService = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const serviceList = [
    {
      serviceId: "Cyber_Incident_Monitoring",
      icon: faBell,
      title: t("home.our_services.service2.title"),
      description: t("home.our_services.service2.description"),
    },
    {
      serviceId: "Cyber_Incident_Response",
      icon: faExclamationTriangle,
      title: t("home.our_services.service3.title"),
      description: t("home.our_services.service3.description"),
    },
    {
      serviceId: "Cyber_Threat_Analysis",
      icon: faLock,
      title: t("home.our_services.service4.title"),
      description: t("home.our_services.service4.description"),
    },
    {
      serviceId: "SOC_as_a_Service",
      icon: faTools,
      title: t("home.our_services.service6.title"),
      description: t("home.our_services.service6.description"),
    },
    {
      serviceId: "Threat_Hunting",
      icon: faChartBar,
      title: t("home.our_services.service5.title"),
      description: t("home.our_services.service5.description"),
    },

    {
      serviceId: "Cyber_Defense_Training",
      icon: faCogs,
      title: t("home.our_services.service7.title"),
      description: t("home.our_services.service7.description"),
    },
    {
      serviceId: "Consultancy_Service",
      icon: faUsersCog,
      title: t("home.our_services.service8.title"),
      description: t("home.our_services.service8.description"),
    },
    {
      serviceId: "Pro_Active_Vulnerability_Analysis",
      icon: faBug,
      title: t("home.our_services.service1.title"),
      description: t("home.our_services.service1.description"),
    },
  ];

  const productList = [
    {
      productId: "National_Cyber_Threat_Intelligence_Database",
      icon: faDatabase,
      title: t("home.our_products.product1.title"),
      description: t("home.our_products.product1.description"),
    },
    {
      productId: "National_Cyber_Incident_Analysis_Reports",
      icon: faFileAlt,
      title: t("home.our_products.product2.title"),
      description: t("home.our_products.product2.description"),
    },
    {
      productId: "National_Incident_Reporting_Management_Platform",
      icon: faShieldAlt,
      title: t("home.our_products.product3.title"),
      description: t("home.our_products.product3.description"),
    },
  ];

  const ServiceCard = ({ service, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{
        y: -8,
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
      className="service-circuit-card bg-white p-8 rounded-xl shadow-lg transition-all duration-300 border-b-4 border-primary overflow-hidden group relative flex flex-col items-center text-center h-[280px] sm:h-[320px]"
    >
      <div className="absolute -right-10 -top-10 w-20 h-20 bg-gradient-to-br from-primary/30 to-primary/5 rounded-full"></div>
      <div className="relative z-20 flex flex-col h-full">
        <div className="flex flex-col items-center">
          <div className="bg-primary p-4 rounded-lg text-white transform transition-transform group-hover:rotate-6 group-hover:scale-110 mb-6 inline-flex justify-center items-center relative z-30">
            <FontAwesomeIcon icon={service.icon} className="text-2xl" />
          </div>
          <h3 className="text-h-6 font-bold text-heading mb-3 group-hover:text-primary transition-colors font-nunito-sans relative z-30">
            {service.title}
          </h3>
          <p className="text-body-big text-paragraph leading-relaxed font-catamaran line-clamp-3 overflow-hidden relative z-30">
            {service.description}
          </p>
        </div>
        <div className="mt-4 relative z-30">
          <Link to={`/services/${encodeURIComponent(service.serviceId)}`}>
            <motion.button
              className="text-body-big text-primary cursor-pointer"
              whileHover={{ color: "#2Ba8CB", transition: { duration: 0.3 } }}
            >
              {t("home.our_services.button")}
            </motion.button>
          </Link>
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            whileHover={{ opacity: 1, width: "100%" }}
            className="h-0.5 bg-primary/20 mt-2"
          />
        </div>
      </div>
    </motion.div>
  );

  ServiceCard.propTypes = {
    service: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
  };

  return (
    <>
      <MetaTags title="Services — Ethio-CERT" description="Catalogue of Ethio-CERT core cybersecurity services and national cyber defense products." url="https://ethiocert.gov.et/our-service" />
      <PageHeader />
      <GradientBanner
        title={t("home.our_services.title")}
        description={t("home.our_services.subtitle_2")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.our_services"), link: null },
        ]}
        backgroundIcons={[faCogs, faTools, faChartBar]}
      />
      <div className="px-dynamic my-10">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="text-center">
              <h2 className="text-heading text-h-3 font-catamaran font-bold mb-6 leading-none">
                {t("home.our_services.title")}
              </h2>
              <div className="flex items-center justify-center mb-6 md:mb-8">
                <div className="w-1/4 border-t border-dashed border-gray-300" />
                <FontAwesomeIcon
                  icon={faCogs}
                  className="text-primary text-4xl md:text-5xl mx-4"
                />
                <div className="w-1/4 border-t border-dashed border-gray-300" />
              </div>
              <p className="text-paragraph text-body font-nunito-sans max-w-2xl mx-auto px-2 text-center">
                {t("home.our_services.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-12">
              {" "}
              {/* Simplified to 2 columns */}
              {serviceList.map((service, i) => (
                <ServiceCard key={i} service={service} index={i} />
              ))}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 max-lg:hidden pb-12">
            <div className="sticky top-24 max-xl:top-32">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl p-6 lg:p-8 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 opacity-10">
                  <svg
                    width="200"
                    height="200"
                    viewBox="0 0 200 200"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill="#4F46E5"
                      d="M40,0 L60,0 L100,40 L40,100 L0,60 Z"
                    />
                    <circle cx="150" cy="50" r="30" fill="#4F46E5" />
                    <rect
                      x="120"
                      y="100"
                      width="60"
                      height="60"
                      fill="#4F46E5"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-8 text-gray-800 border-b pb-4">
                  {t("home.our_products.title")}
                </h3>
                <div className="space-y-4 relative z-10">
                  {productList.map((product, i) => (
                    <div
                      key={i}
                      className="group cursor-pointer hover:bg-gray-50 p-3 rounded-xl transition-all duration-300"
                      onClick={() => navigate(`/products/${product.productId}`)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={product.icon}
                            className="text-primary text-xl"
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium font-nunito-sans text-gray-800 group-hover:text-primary transition-colors duration-300">
                            {product.title}
                          </h4>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {product.description}
                          </p>
                        </div>
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="text-primary text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OurService;
